import { useCallback } from 'react';
import type { FieldArrayWithId, UseFieldArrayAppend, UseFieldArrayInsert, UseFieldArrayMove, UseFieldArrayRemove, UseFieldArrayUpdate } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import type { SequenceFormFields } from '@/validations/sequence';

type StepControls = {
  append: UseFieldArrayAppend<SequenceFormFields, 'steps'>;
  insert: UseFieldArrayInsert<SequenceFormFields, 'steps'>;
  remove: UseFieldArrayRemove;
  move: UseFieldArrayMove;
  update: UseFieldArrayUpdate<SequenceFormFields, 'steps'>;
  fields: FieldArrayWithId<SequenceFormFields, 'steps', 'id'>[];
};

/**
 * Hook to handle step management operations (add, remove, move, etc.)
 * Now works alongside useGrammarTextareaHandlers for keyboard event handling
 */
function makeStep(text: string): SequenceFormFields['steps'][number] {
  return {
    text,
    id: uuidv4(),
    imageInfo: null,
    hasCheckpointPrompt: false,
  };
}

export default function useStepKeyboardNavigation(stepControls: StepControls) {
  const { append, insert, remove, move, update, fields } = stepControls;

  /**
   * Focus a specific step's textarea, placing the caret at the end (default)
   * or the start of the text.
   */
  const focusStep = useCallback((index: number, caret: 'start' | 'end' = 'end') => {
    setTimeout(() => {
      const textarea = document.querySelector(`textarea[name="steps.${index}.text"]`) as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
        const position = caret === 'start' ? 0 : textarea.value.length;
        textarea.setSelectionRange(position, position);
      }
    }, 0);
  }, []);

  /**
   * Create a new empty step at the end of the sequence
   */
  const addStep = useCallback(() => {
    const stepId = uuidv4();
    append({
      text: '',
      id: stepId,
      imageInfo: null,
      hasCheckpointPrompt: false,
    });

    // Focus the newly added step
    setTimeout(() => {
      const lastIndex = fields.length;
      focusStep(lastIndex);
    }, 0);
  }, [append, fields.length, focusStep]);

  /**
   * Add a step at a specific index with optional text
   */
  const addStepAtIndex = useCallback((index: number, text?: string) => {
    const stepId = uuidv4();
    insert(index, {
      text: text || '',
      id: stepId,
      imageInfo: null,
      hasCheckpointPrompt: false,
    });
  }, [insert]);

  /**
   * Split a step at the caret: `before` stays in the current step, `after`
   * moves into a new step inserted right after it (Enter pressed mid-text).
   */
  const splitStep = useCallback((index: number, before: string, after: string) => {
    const current = fields[index];
    if (current === undefined) {
      return;
    }
    update(index, { ...current, text: before });
    insert(index + 1, makeStep(after));
    focusStep(index + 1, 'start');
  }, [fields, update, insert, focusStep]);

  /**
   * Insert pasted multi-line text as one step per line.
   * If the current step already has text it is kept and the lines are added as
   * new steps after it; otherwise the empty current step is replaced (its first
   * line becomes the current step, the rest are inserted after).
   */
  const insertPastedSteps = useCallback((index: number, texts: string[]) => {
    const [firstLine, ...restLines] = texts;
    if (firstLine === undefined) {
      return;
    }

    const current = fields[index];
    const currentHasText = (current?.text ?? '').length > 0;

    if (currentHasText === true) {
      insert(index + 1, texts.map(makeStep));
      return;
    }

    if (current !== undefined) {
      update(index, { ...current, text: firstLine });
    }
    if (restLines.length > 0) {
      insert(index + 1, restLines.map(makeStep));
    }
  }, [fields, insert, update]);

  /**
   * Remove a step at the specified index
   */
  const removeStep = useCallback((index: number) => {
    remove(index);
  }, [remove]);

  /**
   * Move a step from one index to another
   */
  const moveStep = useCallback((fromIndex: number, toIndex: number) => {
    if (toIndex >= 0 && toIndex < fields.length) {
      move(fromIndex, toIndex);
    }
  }, [fields.length, move]);

  /**
   * Merge the current step's text with the previous step and remove the current step
   */
  const mergeWithPreviousStep = useCallback((currentStepIndex: number, currentStepText: string) => {
    if (currentStepIndex <= 0 || currentStepIndex >= fields.length) {
      return; // Can't merge first step or invalid index
    }

    const previousStep = fields[currentStepIndex - 1];
    if (!previousStep) {
      return;
    }

    // Get the previous step's current text
    const previousStepText = previousStep.text || '';

    // Merge texts with a space in between (only if both have content)
    const mergedText = previousStepText && currentStepText
      ? `${previousStepText} ${currentStepText}`
      : previousStepText + currentStepText;

    // Update the previous step with merged text
    update(currentStepIndex - 1, {
      ...previousStep,
      text: mergedText,
    });

    // Remove the current step
    remove(currentStepIndex);

    // Focus the previous step and position cursor at the end of the original text
    setTimeout(() => {
      const textarea = document.querySelector(`textarea[name="steps.${currentStepIndex - 1}.text"]`) as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
        // Position cursor at the end of the previous text (before the merged text)
        const cursorPosition = previousStepText.length + (previousStepText ? 1 : 0); // +1 for the space if there was previous text
        textarea.setSelectionRange(cursorPosition, cursorPosition);
      }
    }, 0);
  }, [fields, update, remove]);

  return {
    // Step management functions
    addStep,
    addStepAtIndex,
    splitStep,
    insertPastedSteps,
    removeStep,
    moveStep,
    focusStep,
    mergeWithPreviousStep,
  };
}
